import {
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  type SyntheticEvent,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type Ref,
  type CSSProperties,
} from 'react';

import { useLinkComponent } from '@eneco/link';
import { type LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';
import reactToText from 'react-to-text';

import {
  tileBarBase,
  tileBarRecipe,
  tileItemBase,
  tileItemRecipe,
  tileItemContainer,
  tileItemLi,
  classNames,
  hoverContent as hoverContentClass,
} from './TileBar.css';
import { useMediaQuery } from '../../hooks';
import { mergeRefs } from '../../util';
import { getClassNames } from '../../util/css';
import { type RecipeVariants } from '../../vanilla-extract.css';

export interface Props extends RecipeVariants<typeof tileBarRecipe> {
  children: ReactNode[];
  className?: never;
}

type TileBarContextType = {
  maxHeight: number;
  setMaxHeight: Dispatch<SetStateAction<number>>;
  isElevated: boolean;
  direction: 'row' | 'column';
};

const TileContext = createContext<TileBarContextType>({
  maxHeight: 0,
  setMaxHeight: () => {},
  isElevated: true,
  direction: 'row',
});

const useTilebarContext = (): TileBarContextType => useContext(TileContext);

const TileBarComponent = forwardRef<HTMLUListElement, Props>(
  ({ children, isElevated = true, direction = 'row' }, ref) => {
    const [maxHeight, setMaxHeight] = useState(0);

    const sm = useMediaQuery('sm');
    const md = useMediaQuery('md');
    const lg = useMediaQuery('lg');
    const xl = useMediaQuery('xl');
    const breakpoints = { sm, md, lg, xl };
    const isCurrentlyRow =
      direction === 'row' ||
      Object.entries(direction).some(
        // @ts-ignore Object.entries breaks the typing of the element
        element => element[1] === 'row' && element[0] in breakpoints && breakpoints[element[0]],
      );

    const classNames = getClassNames(tileBarBase, tileBarRecipe({ isElevated, direction }));
    const style: CSSProperties = isCurrentlyRow && maxHeight > 0 ? { height: `${maxHeight}px` } : {};

    // Determine the current direction value for data attribute
    const currentDirection = typeof direction === 'string' ? direction : 'row';
    const currentElevated = typeof isElevated === 'boolean' ? isElevated : true;

    const tileContextValue = useMemo(
      () => ({
        maxHeight,
        setMaxHeight,
        isElevated: currentElevated,
        direction: currentDirection,
      }),
      [maxHeight, setMaxHeight, currentElevated, currentDirection],
    );

    return (
      <TileContext.Provider value={tileContextValue}>
        <ul
          ref={ref}
          className={classNames}
          style={style}
          data-direction={currentDirection}
          data-elevated={currentElevated.toString()}>
          {children}
        </ul>
      </TileContext.Provider>
    );
  },
);

TileBarComponent.toString = () => `.${classNames.tileBar}`;

type TileButtonProps = {
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  ariaLabel?: HTMLAttributes<HTMLElement>['aria-label'];
  linkType?: never;
  linkValue?: never;
};

type TileAnchorProps = {
  type?: never;
  linkValue: LinkFieldValue;
  linkType: LinkFieldValue['linktype'];
  ariaLabel?: HTMLAttributes<HTMLElement>['aria-label'];
};

type ItemProps = {
  onClick?(e: SyntheticEvent): void;
  children: ReactNode;
  /** Content shown when a tile is hovered. Only works for the row orientation. */
  hoverContent?: ReactNode;
  linkValue?: Pick<LinkFieldValue, 'anchor' | 'href' | 'querystring'>;
} & Pick<RecipeVariants<typeof tileItemRecipe>, 'alignY'> &
  (TileButtonProps | TileAnchorProps);

const TileBarItem = forwardRef<HTMLAnchorElement, ItemProps>(
  ({ children, linkType, onClick, type = 'button', hoverContent, alignY = 'center', linkValue, ariaLabel }, ref) => {
    const { maxHeight, setMaxHeight, isElevated, direction } = useTilebarContext();

    const localRef: Ref<HTMLButtonElement | HTMLAnchorElement> = useRef(null);
    const mergedRef = mergeRefs([ref, localRef]);

    useEffect(() => {
      const checkHeight = () => {
        if (localRef?.current?.offsetHeight && localRef.current.offsetHeight > maxHeight) {
          setMaxHeight(localRef.current.offsetHeight);
        }
      };

      window.addEventListener('resize', checkHeight);
      checkHeight();
      return () => {
        window.removeEventListener('resize', checkHeight);
      };
    }, [maxHeight, setMaxHeight]);

    const label = reactToText(children);
    const itemClassName = getClassNames(tileItemBase, tileItemRecipe({ alignY, isElevated, direction }));
    const Link = useLinkComponent();

    if (linkValue && linkType) {
      return (
        <li className={tileItemLi}>
          <Link linkValue={linkValue} linkType={linkType}>
            <a
              href={linkValue.href || '#'}
              className={itemClassName}
              data-elevated={String(isElevated)}
              data-direction={direction}
              target={linkValue.target}
              rel={linkValue?.target === '_blank' ? 'noreferrer' : ''}
              onClick={onClick}
              aria-label={ariaLabel ?? label}
              ref={mergedRef as Ref<HTMLAnchorElement>}>
              <div className={tileItemContainer}>
                {children}
                <div className={hoverContentClass}>{hoverContent}</div>
              </div>
            </a>
          </Link>
        </li>
      );
    }

    return (
      <li className={tileItemLi}>
        <button
          className={itemClassName}
          data-elevated={String(isElevated)}
          data-direction={direction}
          type={type}
          onClick={onClick}
          aria-label={ariaLabel ?? label}
          ref={mergedRef as Ref<HTMLButtonElement>}>
          <div className={tileItemContainer}>
            {children}
            <div className={hoverContentClass}>{hoverContent}</div>
          </div>
        </button>
      </li>
    );
  },
);
export const TileBar = { ...TileBarComponent, Item: TileBarItem } as typeof TileBarComponent & { Item: typeof TileBarItem };
